import { Fragment, useEffect, useState, useMemo, useCallback } from "react";
import React from "react";
import DataTable from "../../../../components/ui/table/DataTable";
import FilterModal from "../../../../components/ui/popup/FilterModal";
import EmptyState from "../../../../components/ui/pages/EmptyState";
import GhostCardContainer from "../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../../components/ui/loading/skeletons/GhostDataTable";
import LoadingToRealTransitionWrapper from "../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import { fetchPaymentHistory } from "../../../../services/payments/paymentPayout";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth";
import { getLanguageContent } from "../../../../utils/language/languageSelector";
import { FILTER_SCHEMA } from "../../../../utils/schemas/language/components/filterSchema";

const Payments = () => {
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [lastPaymentId, setLastPaymentId] = useState(null);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Active filter rule states
  const [filters, setFilters] = useState({
    date: "",
    status: "",
  });

  // Intent-locked filter modifications state window
  const [draftFilters, setDraftFilters] = useState({
    date: "",
    status: "",
  });

  // Re-balanced widths to ensure the column grid aligns perfectly with row flex dimensions
  const headers = [
    { label: "Date", key: "date", sortable: true, type: "tag", width: "140px" },
    { label: "Service", key: "service", sortable: true, width: "140px" },
    { label: "Amount", key: "amount", sortable: true, width: "140px" },
    {
      label: "Payment Method",
      key: "paymentMethod",
      sortable: false,
      width: "250px",
    },
    {
      label: "Status",
      key: "status", // Matches DataTableRow condition natively
      sortable: true,
      type: "filing_status",
      width: "140px",
    },
  ];

  // ============================================================
  // DATA INTERFACE PARSING & TRANSFORM LOGIC
  // ============================================================
  const transformPayment = (payment) => {
    let formattedDate = "";
    if (payment.created) {
      let date;
      if (typeof payment.created === "number") {
        date = new Date(payment.created * 1000);
      } else {
        date = new Date(payment.created);
      }

      if (!isNaN(date.getTime())) {
        formattedDate = `${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(date.getDate()).padStart(2, "0")}-${date.getFullYear()}`;
      }
    }

    let formattedAmount = "$0.00";
    if (payment.amount) {
      const amountInDollars =
        typeof payment.amount === "number"
          ? payment.amount
          : parseFloat(payment.amount);
      formattedAmount = `$${amountInDollars.toFixed(2)}`;
    }

    let formattedPaymentMethod = "N/A";
    if (payment.payment_method) {
      if (typeof payment.payment_method === "string") {
        formattedPaymentMethod = payment.payment_method;
      } else if (typeof payment.payment_method === "object") {
        const pm = payment.payment_method;
        const brand = pm.brand
          ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
          : "Card";
        const last4 = pm.last4 || "****";

        // Safely extract expiration variables if they exist in payload
        let expirationText = "";
        if (pm.exp_month && pm.exp_year) {
          const paddedMonth = String(pm.exp_month).padStart(2, "0");
          const shortYear = String(pm.exp_year).slice(-2);
          expirationText = `Card Expire: ${paddedMonth}/${shortYear}`;
        }

        formattedPaymentMethod = `${brand} ****${last4} | ${expirationText}`;
      }
    }

    // Map Stripe statuses directly to your global theme's pre-configured color states
    const rawStatus = payment.status ? payment.status.toLowerCase() : "";
    let formattedStatus = "Draft";

    if (rawStatus === "succeeded") {
      formattedStatus = "Completed"; // Triggers green background badge style
    } else if (rawStatus === "pending") {
      formattedStatus = "Active"; // Triggers yellow background badge style
    } else if (rawStatus === "failed") {
      formattedStatus = "Cancelled"; // Triggers red background badge style
    }

    return {
      id: payment.id,
      date: formattedDate,
      service: payment.service || "N/A",
      amount: formattedAmount,
      paymentMethod: formattedPaymentMethod,
      status: formattedStatus, // Directly matches header key config "status"

      date_value:
        payment.created && typeof payment.created === "number"
          ? payment.created
          : new Date(payment.created).getTime() / 1000,
      amount_value: payment.amount ? parseFloat(payment.amount) : 0,
    };
  };

  // ============================================================
  // BACKEND API PAGINATION LIFECYCLE
  // ============================================================
  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startingAfter = currentPage === 1 ? null : lastPaymentId;
      const response = await fetchPaymentHistory(startingAfter);

      if (response.status === 401) return;

      const data = await response.json();

      if (response.status === 200) {
        const transformedPayments = data.payments.map(transformPayment);
        setPayments(transformedPayments);

        if (transformedPayments.length > 0) {
          setLastPaymentId(
            transformedPayments[transformedPayments.length - 1].id,
          );
        }
        setHasMore(data.has_more || data.hasMore || false);
      } else {
        setError(data.message || "Failed to fetch payment history");
        setPayments([]);
      }
    } catch (err) {
      console.error("Error fetching payment history:", err);
      setError("Failed to fetch payment history. Please try again later.");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, lastPaymentId]);

  useRefreshOnAuth(loadPayments);
  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  // Client side row array filtering pipeline
  useEffect(() => {
    let filtered = payments;

    if (filters.date) {
      const [year, month, day] = filters.date.split("-");
      const formattedDate = `${month}-${day}-${year}`;
      filtered = filtered.filter((payment) => payment.date === formattedDate);
    }

    if (filters.status) {
      filtered = filtered.filter(
        (payment) =>
          payment.status.toLowerCase() === filters.status.toLowerCase(),
      );
    }

    setFilteredPayments(filtered);
  }, [payments, filters]);

  // Sync pagination cursor back on filter updates
  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(
      (value) => value !== "",
    );
    if (hasActiveFilters && currentPage !== 1) {
      setCurrentPage(1);
      setLastPaymentId(null);
    }
  }, [filters]);

  const totalPagesCalculation = useMemo(() => {
    return hasMore || currentPage > 1
      ? currentPage + (hasMore ? 1 : 0)
      : currentPage;
  }, [hasMore, currentPage]);

  return (
    <Fragment>
      <div className="internal-content-body">
        {error ? (
          <EmptyState
            title="Unable to load payments"
            text="We encountered an issue while fetching your payment history. Please try refreshing the page or contact support if the problem persists."
          />
        ) : (
          <LoadingToRealTransitionWrapper
            isLoading={loading}
            ghost={
              <GhostCardContainer>
                <GhostDataTable
                  headers={headers}
                  rows={4}
                  title={true}
                  description={false}
                  showSearch={false}
                />
              </GhostCardContainer>
            }
          >
            <DataTable
              title="Payments"
              headers={headers}
              rows={filteredPayments}
              filterElement={true}
              onFilterClick={() => {
                setDraftFilters(filters);
                setIsFilterOpen(true);
              }}
              currentPage={currentPage}
              totalPages={totalPagesCalculation}
              onPageChange={(page) => setCurrentPage(page)}
              emptyStateTitle="No payments yet"
              emptyStateText="Your payment history will appear here once you start receiving payments from clients"
            />
          </LoadingToRealTransitionWrapper>
        )}
      </div>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={() => {
          setFilters(draftFilters);
          setIsFilterOpen(false);
        }}
        title={filterContent.payments.title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <input
            className="form-input"
            type="date"
            value={draftFilters.date}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, date: e.target.value }))
            }
          />
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.status}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="">{filterContent.all_statuses}</option>
            <option value="Succeeded">{filterContent.status.succeeded}</option>
            <option value="Pending">{filterContent.status.pending}</option>
            <option value="Failed">{filterContent.status.failed}</option>
          </select>
        </div>
      </FilterModal>
    </Fragment>
  );
};

export default Payments;
